import React, { useEffect } from "react";
import {
    Box, Paper, Typography, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Button, Stack, Chip,
} from "@mui/material";
import { SwapHoriz } from "@mui/icons-material";
import { format } from "date-fns";
import {
    useLazyGetPendingTransferRequestsQuery,
    useRespondToTransferRequestMutation,
} from "store/services/driverApi";

export default function PendingTransferRequestsPanel({ driverId, initialData }) {
    const [fetchRequests, { data, isLoading, isFetching }] =
        useLazyGetPendingTransferRequestsQuery();


    const [respondToTransferRequest, { isLoading: isResponding }] =
        useRespondToTransferRequestMutation();

    useEffect(() => {
        if (driverId) fetchRequests(driverId);
    }, [driverId, fetchRequests]);

    // Poll every 30s
    useEffect(() => {
        if (!driverId) return;
        const interval = setInterval(() => fetchRequests(driverId), 30000);
        return () => clearInterval(interval);
    }, [driverId, fetchRequests]);

    const requests = data?.data || initialData || [];

    const handleRespond = async (id, action) => {
        await respondToTransferRequest({ id, action });
        fetchRequests(driverId);
    };


    return (
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4, boxShadow: 2, height: "100%" }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                    <SwapHoriz sx={{ color: "warning.main", fontSize: 28, mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold" className="text-gray-900 dark:text-white">
                        Pending Transfer Requests
                    </Typography>
                </Box>
                {requests.length > 0 && (
                    <Chip label={requests.length} color="warning" size="small" sx={{ fontWeight: 700 }} />
                )}
            </Box>

            {isLoading || isFetching ? (
                <Typography variant="body2" color="text.secondary">Loading...</Typography>
            ) : requests.length === 0 ? (
                <Box display="flex" flexDirection="column" alignItems="center"
                    justifyContent="center" sx={{ minHeight: 150 }} gap={1}>
                    <SwapHoriz sx={{ fontSize: 80, color: "text.disabled" }} />
                    <Typography variant="body2" color="text.secondary">
                        No pending transfer requests
                    </Typography>
                </Box>
            ) : (
                <Stack spacing={2}>
                    {requests.map((request) => (
                        <Box
                            key={request._id}
                            sx={{
                                border: "1px solid",
                                borderColor: "warning.light",
                                borderRadius: 2,
                                p: 1.5,
                                bgcolor: "warning.50",
                            }}
                        >
                            {/* Header */}
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                <Typography variant="body2" fontWeight={600} color="text.primary">
                                    From: {request.fromDriverName}
                                </Typography>
                                <Stack alignItems="flex-end" spacing={0.25}>
                                    <Typography variant="caption" color="text.secondary">
                                        Requested: {format(new Date(request.requestedAt), "MMM dd, yyyy h:mm a")}
                                    </Typography>
                                    {request.approvedAt && (
                                        <Typography variant="caption" color="success.main">
                                            Approved: {format(new Date(request.approvedAt), "MMM dd, yyyy h:mm a")}
                                        </Typography>
                                    )}
                                    {request.rejectedAt && (
                                        <Typography variant="caption" color="error.main">
                                            Rejected: {format(new Date(request.rejectedAt), "MMM dd, yyyy h:mm a")}
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>

                            {/* Loadings & Shipments */}
                            <TableContainer sx={{ mb: 1.5, maxHeight: 180 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: "action.hover" }}>
                                            <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", py: 0.5 }}>
                                                Loading ID
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 700, fontSize: "0.72rem", py: 0.5 }}>
                                                Shipments
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {request.loadings.map((loading) => (
                                            <TableRow key={loading._id}>
                                                <TableCell sx={{ fontSize: "0.72rem", fontFamily: "monospace", py: 0.5 }}>
                                                    {loading.loadingId}
                                                </TableCell>
                                                <TableCell sx={{ py: 0.5 }}>
                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                        {loading.shipments.map((sh) => (
                                                            <Chip
                                                                key={sh._id}
                                                                label={sh.shipmentId}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{ fontSize: "0.65rem", height: 20, fontFamily: "monospace" }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Accept / Reject */}
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    disabled={isResponding}
                                    onClick={() => handleRespond(request._id, "rejected")}
                                    sx={{ borderRadius: 1, fontSize: "0.72rem" }}
                                >
                                    Reject
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    disabled={isResponding}
                                    onClick={() => handleRespond(request._id, "approved")}
                                    sx={{ borderRadius: 1, fontSize: "0.72rem" }}
                                >
                                    Accept
                                </Button>
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            )}
        </Paper>
    );
}